import { apiClient } from '@/lib/api/client';
import type {
  AssociationGroup,
  CreateAssociationGroupDto,
  UpdateAssociationGroupDto,
  AddProductsToGroupDto,
} from '@/lib/types';

interface ApiResponse<T> {
  data: T;
  message: string;
}

export const associationsService = {
  /**
   * Get all association groups for the current seller
   */
  async getAssociationGroups(): Promise<AssociationGroup[]> {
    const response = await apiClient.get<ApiResponse<AssociationGroup[]>>('/product/associations');
    return response.data?.data ?? [];
  },

  /**
   * Create a new association group
   */
  async createAssociationGroup(data: CreateAssociationGroupDto): Promise<AssociationGroup> {
    const response = await apiClient.post<ApiResponse<AssociationGroup>>('/product/associations', {
      name: data.name,
      shopId: data.shopId,
      description: data.description || data.name,
      variationType: data.variationType || 'related',
      items: data.items,
    });
    return response.data?.data;
  },

  /**
   * Add products to an existing association group
   */
  async addProductsToGroup(data: AddProductsToGroupDto): Promise<void> {
    await apiClient.post('/product/associations/add', {
      productRefIds: data.productRefIds,
      groupId: data.groupId,
      shopId: data.shopId,
    });
  },

  /**
   * Update an association group
   */
  async updateAssociationGroup(data: UpdateAssociationGroupDto): Promise<AssociationGroup> {
    const response = await apiClient.put<ApiResponse<AssociationGroup>>('/product/associations', {
      groupId: data.groupId,
      name: data.name,
      description: data.description,
      variationType: data.variationType,
      items: data.items,
    });
    return response.data?.data;
  },

  /**
   * Remove a product from an association group
   */
  async removeProductFromGroup(groupId: number, productRefId: string): Promise<void> {
    await apiClient.delete('/product/associations/remove', {
      data: { groupId, productRefId },
    });
  },

  /**
   * Delete an entire association group
   */
  async deleteAssociationGroup(groupId: number): Promise<void> {
    await apiClient.delete('/product/associations', {
      data: { groupId },
    });
  },
};
